import type { FieldDefinition } from '@facile-it/shark-morph/components/Form/fields/FieldDefinitions'
import { CheckList } from '@facile-it/shark-morph/components/Form/fields/checkList'
import { ChoiceOption } from '@facile-it/shark-morph/components/Form/fields/choiceOption'
import { Chosen } from '@facile-it/shark-morph/components/Form/fields/chosen'
import { DateTime } from '@facile-it/shark-morph/components/Form/fields/dateTime'
import { doFormat } from '@facile-it/shark-morph/components/Form/fields/dateTime/dateRawFormatter'
import { Files } from '@facile-it/shark-morph/components/Form/fields/files'
import { HTMLArea } from '@facile-it/shark-morph/components/Form/fields/htmlarea'
import { Input } from '@facile-it/shark-morph/components/Form/fields/input'
import { InputTable } from '@facile-it/shark-morph/components/Form/fields/inputTable'
import { Select } from '@facile-it/shark-morph/components/Form/fields/select'
import type { SelectClassic } from '@facile-it/shark-morph/components/Form/fields/select/components/SelectClassic'
import type { SelectExploded } from '@facile-it/shark-morph/components/Form/fields/select/components/SelectExploded'
import type { SelectIcon } from '@facile-it/shark-morph/components/Form/fields/select/components/SelectIcon'
import { TextArea } from '@facile-it/shark-morph/components/Form/fields/textarea'
import type { VIEWPORT_SIZE } from '@facile-it/shark-morph/components/constants'
import * as O from 'fp-ts/Option'
import { pipe } from 'fp-ts/function'
import moment from 'moment-timezone'
import type { ReactNode } from 'react'
import { identity } from 'rxjs'
import type { updateSubject } from './formBuilder'
import type { Validator } from './validators/Validator'
import { buildSingleValidator } from './validators/Validator'
import { momentValidator } from './validators/constraints/momentValidator'
import { notBlankValidator } from './validators/constraints/notBlankValidator'

export namespace Unit {
    export type UnionFields =
        | { c: 'input'; p: Input.Props; v: Input.valueType; d: Input.dataType; i: Input.dataType }
        | {
              c: 'selectClassic'
              p: SelectClassic.Props
              v: SelectClassic.valueType
              d: SelectClassic.dataType
              i: SelectClassic.valueType | SelectClassic.dataType
          }
        | {
              c: 'selectIcon'
              p: SelectIcon.Props
              v: SelectIcon.valueType
              d: SelectIcon.dataType
              i: SelectIcon.valueType | SelectClassic.dataType
          }
        | {
              c: 'selectExploded'
              p: SelectExploded.Props
              v: SelectExploded.valueType
              d: SelectExploded.dataType
              i: SelectExploded.valueType | SelectClassic.dataType
          }
        | {
              c: 'chosen'
              p: Omit<Chosen.Props, 'onFilter' | 'data'>
              v: Chosen.valueType
              d: Chosen.dataType
              i: Chosen.dataType
          }
        | {
              c: 'dateTime'
              p: DateTime.Props
              v: DateTime.valueType
              d: DateTime.dataType
              i: DateTime.valueType | DateTime.dataType
          }
        | {
              c: 'files'
              p: Files.Props
              v: Files.valueType
              d: Files.dataType
              i: Files.dataType
          }
        | {
              c: 'choiceOption'
              p: ChoiceOption.Props
              v: ChoiceOption.valueType
              d: ChoiceOption.dataType
              i: ChoiceOption.valueType | ChoiceOption.dataType
          }
        | {
              c: 'textArea'
              p: TextArea.Props
              v: TextArea.valueType
              d: TextArea.dataType
              i: TextArea.dataType
          }
        | {
              c: 'htmlArea'
              p: HTMLArea.Props
              v: HTMLArea.valueType
              d: HTMLArea.dataType
              i: HTMLArea.dataType
          }
        | {
              c: 'inputTable'
              p: InputTable.Props<string>
              v: InputTable.valueType<string>
              d: InputTable.dataType<string>
              i: InputTable.dataType<string>
          }
        | {
              c: 'checkList'
              p: CheckList.Props<string>
              v: CheckList.valueType<string>
              d: CheckList.dataType<string>
              i: CheckList.dataType<string>
          }

    export type pickValueType<c extends UnionFields['c']> = Extract<Unit.UnionFields, { c: c }>['v']

    export type pickDateType<c extends UnionFields['c']> = Extract<Unit.UnionFields, { c: c }>['d']

    export type pickProps<c extends UnionFields['c']> = Extract<Unit.UnionFields, { c: c }>['p']

    export type pickInitDateType<c extends UnionFields['c']> = Extract<Unit.UnionFields, { c: c }>['i']

    export type pickConfig<c extends UnionFields['c']> = Extract<Unit.UnionFields, { c: c }>

    export type UnitUnion = HtmlUnit | FormUnit<UnionFields['c'], string>

    export interface FormUnit<
        componentType extends UnionFields['c'],
        tagsType extends string,
        notBlank extends boolean = boolean,
        config extends UnionFields = pickConfig<componentType>,
    > {
        unitType: 'form'
        componentType: componentType

        component: React.ComponentType<config['p']>
        props: Omit<config['p'], 'onChange' | 'onFocus' | 'onBlur' | 'name' | 'value'>
        label?: ReactNode

        notBlank: notBlank
        validators?: ValidatorsType<componentType>

        tags: tagsType[]

        value: pickValueType<componentType>
        data: pickDateType<componentType>

        showOnlyValidation: 'all' | 'error-warning'

        waitToShowNegativeValidation?: number
    }

    export interface HtmlUnit {
        unitType: 'html'
        element: React.ReactNode
    }

    export interface FormUnitOmited<
        componentType extends UnionFields['c'],
        tagsType extends string,
        notBlank extends boolean = boolean,
        unit extends FormUnit<componentType, tagsType, notBlank> = FormUnit<componentType, tagsType, notBlank>,
    > {
        componentType: unit['componentType']

        props: unit['props']
        label?: unit['label']

        notBlank: unit['notBlank']
        validators?: unit['validators']

        tags: unit['tags']

        initData?: pickInitDateType<componentType>

        showOnlyValidation?: unit['showOnlyValidation']

        waitToShowNegativeValidation?: unit['waitToShowNegativeValidation']
    }

    export interface ValidatorsType<componentType extends UnionFields['c']> {
        [key: string]: Validator.validatorSingleBuilded<pickValueType<componentType>, pickDateType<componentType>>
    }

    export interface ViewType {
        col:
            | {
                  xs?: VIEWPORT_SIZE
                  sm?: VIEWPORT_SIZE
                  md?: VIEWPORT_SIZE
                  lg?: VIEWPORT_SIZE
                  xl?: VIEWPORT_SIZE
                  xxl?: VIEWPORT_SIZE
              }
            | VIEWPORT_SIZE
    }
}

export class Unit {
    private static fields: { [key in Unit.UnionFields['c']]: React.ComponentType<any> } = {
        input: Input,
        selectClassic: Select.Classic,
        selectExploded: Select.Exploded,
        selectIcon: Select.Icon,
        choiceOption: ChoiceOption,
        chosen: Chosen,
        dateTime: DateTime,
        textArea: TextArea,
        htmlArea: HTMLArea,
        files: Files,
        inputTable: InputTable,
        checkList: CheckList,
    }

    static buildHtmlUnit(config: Omit<Unit.HtmlUnit, 'unitType'>): Unit.HtmlUnit {
        return {
            ...config,
            unitType: 'html',
        }
    }

    static buildFormUnit<
        componentType extends Unit.UnionFields['c'],
        tagsType extends string,
        notBlank extends boolean,
        config extends Unit.UnionFields = Unit.pickConfig<componentType>,
    >(
        config: Unit.FormUnitOmited<componentType, tagsType, notBlank>
    ): Unit.FormUnit<componentType, tagsType, notBlank> {
        const initData = this.autoFillInitData(config.componentType, config.initData, config.props)

        const pConfig: Unit.FormUnit<componentType, tagsType, notBlank> = {
            unitType: 'form',
            componentType: config.componentType,
            notBlank: config.notBlank,
            props: config.props,
            tags: config.tags,
            label: config.label,
            validators: config.validators,
            waitToShowNegativeValidation: config.waitToShowNegativeValidation,
            component: this.fields[config.componentType] as Unit.FormUnit<
                componentType,
                tagsType,
                notBlank
            >['component'],
            data: this.unsafeInitDataToData(config.componentType, initData, config.props),
            value: this.unsafeInitDataToValue(config.componentType, initData, config.props),
            showOnlyValidation: pipe(
                O.fromNullable(config.showOnlyValidation),
                O.match(
                    () => 'all',
                    s => s
                )
            ),
        } as Unit.FormUnit<componentType, tagsType, notBlank>

        return this.decorateValidators(pConfig)
    }

    static buildUpdaterFromInitData<
        componentType extends Unit.UnionFields['c'],
        config extends Unit.UnionFields = Unit.pickConfig<componentType>,
    >(
        componentType: componentType,
        initValue: O.Option<config['i']>,
        props: Unit.pickProps<componentType>
    ): updateSubject<componentType> {
        return pipe(
            initValue,
            O.map(
                (v): updateSubject<componentType> =>
                    ({
                        current: {
                            data: this.unsafeInitDataToData(componentType, v, props),
                            value: this.unsafeInitDataToValue(componentType, v, props),
                            forceOnInt: false,
                        },
                        disabled: false,
                    }) as updateSubject<componentType>
            ),
            O.match(() => ({ disabled: false }) as updateSubject<componentType>, identity)
        )
    }

    static buildUpdaterFromOptions<
        componentType extends 'selectClassic',
        config extends Unit.UnionFields = Unit.pickConfig<componentType>,
    >(
        componentType: componentType,
        options: O.Option<FieldDefinition.Option[]>,
        props: config['p'],
        reset = false
    ): updateSubject<componentType> {
        return pipe(
            options,
            O.map((o): [config['i'], FieldDefinition.Option[]] => [
                this.autoFillInitData(componentType, undefined, { ...props, options: o }),
                o,
            ]),
            O.map(
                ([v, o]): updateSubject<componentType> => ({
                    ...pipe(
                        O.fromNullable(v),
                        O.match(
                            (): any =>
                                reset
                                    ? {
                                          current: {
                                              data: { value: undefined, label: undefined },
                                              value: undefined,
                                              forceOnInt: true,
                                          },
                                      }
                                    : {},
                            vv => ({
                                current: {
                                    data: this.unsafeInitDataToData(componentType, vv, { ...props, options: o }),
                                    value: this.unsafeInitDataToValue(componentType, vv, { ...props, options: o }),
                                    forceOnInt: reset,
                                },
                            })
                        )
                    ),
                    disabled: false,
                    options: o,
                })
            ),
            O.match(() => ({}) as updateSubject<componentType>, identity)
        )
    }

    static buildUpdaterDisabledToggle(disabled: boolean): updateSubject<'selectClassic'> {
        return { disabled: disabled }
    }

    static buildUpdaterEmptyOptions(): updateSubject<'selectClassic'> {
        return { disabled: true, options: [] }
    }

    static buildUpdaterEmptyOptionsAndResetValue(forceOnInt = false): updateSubject<'selectClassic'> {
        return { disabled: true, options: [], current: { value: undefined, data: undefined, forceOnInt: forceOnInt } }
    }

    private static decorateValidators<
        componentType extends Unit.UnionFields['c'],
        tagsType extends string,
        notBlank extends boolean,
    >(config: Unit.FormUnit<componentType, tagsType, notBlank>): typeof config {
        if (config.componentType === 'dateTime') {
            config.validators = {
                ...config.validators,
                momentValid: buildSingleValidator(momentValidator, {}) as Validator.validatorSingleBuilded<
                    Unit.pickValueType<componentType>,
                    Unit.pickDateType<componentType>
                >,
            }
        }

        if (config.notBlank) {
            config.validators = {
                ...config.validators,
                notBlank: buildSingleValidator(notBlankValidator, {}),
            }
        }

        return config
    }

    private static autoFillInitData(
        componentType: Unit.UnionFields['c'],
        initData: Unit.pickInitDateType<typeof componentType>,
        props: Unit.pickProps<typeof componentType>
    ) {
        if (typeof initData !== 'undefined') {
            return initData
        }

        switch (componentType) {
            case 'selectClassic':
                return pipe(
                    O.fromNullable((props as Unit.pickProps<typeof componentType>).options),
                    O.filter(os => os.length === 1),
                    O.chainNullableK(os => os[0]),
                    O.chainNullableK(o => o.value),
                    O.filter(v => v !== ''),
                    O.toUndefined
                )
            default:
                return undefined
        }
    }

    private static unsafeInitDataToValue(
        componentType: Unit.UnionFields['c'],
        initData: Unit.pickInitDateType<typeof componentType>,
        props: Unit.pickProps<typeof componentType>
    ) {
        if (this.isNone(initData)) {
            return undefined
        }

        switch (componentType) {
            case 'selectClassic':
            case 'selectExploded':
            case 'selectIcon':
                return this.dataOptionToValue(componentType, initData as Unit.pickInitDateType<typeof componentType>)
            case 'dateTime': {
                const propsR = props as Unit.pickProps<typeof componentType>

                if (typeof initData === 'string' && initData.match(/^[0-9]{4}-[0-9]{2}-[0-9]{2}/)) {
                    return doFormat(propsR.dateType, moment(initData))
                }

                if (typeof initData === 'string') {
                    return doFormat(propsR.dateType, moment(initData, 'YYYYMMDDHHmmSS'))
                }

                return doFormat(propsR.dateType, initData as moment.Moment)
            }
            case 'chosen':
                if (typeof (initData as FieldDefinition.DataOption).value === 'undefined') {
                    return undefined
                }

                return (initData as FieldDefinition.DataOption).value
            case 'choiceOption':
            case 'textArea':
            case 'htmlArea':
            case 'input':
            case 'files':
            case 'inputTable':
            case 'checkList':
                return initData
        }
    }

    private static unsafeInitDataToData(
        componentType: Unit.UnionFields['c'],
        initData: Unit.pickInitDateType<typeof componentType>,
        props: Unit.pickProps<typeof componentType>
    ) {
        if (this.isNone(initData)) {
            return undefined
        }

        switch (componentType) {
            case 'selectClassic':
            case 'selectExploded':
            case 'selectIcon':
                return this.initDataToDataOption(
                    componentType,
                    initData as Unit.pickInitDateType<typeof componentType>,
                    props as Unit.pickProps<typeof componentType>
                )
            case 'dateTime':
                if (typeof initData === 'string' && initData.match(/^[0-9]{4}-[0-9]{2}-[0-9]{2}/)) {
                    return moment(initData)
                }

                if (typeof initData === 'string') {
                    return moment(initData, 'YYYYMMDDHHmmSS')
                }

                return initData
            case 'chosen':
                if (typeof (initData as FieldDefinition.DataOption).value === 'undefined') {
                    return undefined
                }

                return initData
            case 'choiceOption':
            case 'textArea':
            case 'htmlArea':
            case 'input':
            case 'files':
            case 'inputTable':
            case 'checkList':
                return initData
        }
    }

    private static dataOptionToValue(
        componentType: 'selectClassic' | 'selectExploded' | 'selectIcon',
        initData: Unit.pickInitDateType<typeof componentType>
    ): Unit.pickValueType<typeof componentType> {
        if (this.isNone(initData)) {
            return undefined
        }

        if (Array.isArray(initData)) {
            return initData.map(d => (typeof d === 'object' ? (d as FieldDefinition.DataOption).value : d))
        }

        return typeof initData === 'string' || typeof initData === 'number' ? initData : initData.value
    }

    public static initDataToDataOption(
        componentType: 'selectClassic' | 'selectExploded' | 'selectIcon',
        initData: Unit.pickInitDateType<typeof componentType>,
        props: Unit.pickProps<typeof componentType>
    ): Unit.pickDateType<typeof componentType> {
        if (this.isNone(initData)) {
            return undefined
        }

        const findLabel = (v: any): string => {
            const { options } = props

            if (!options) {
                return ''
            }

            return (options as FieldDefinition.DataOption[])
                .filter(o => o.value === v)
                .map(o => o.label)
                .join(' ')
        }

        if (Array.isArray(initData)) {
            return initData.map(v =>
                typeof v === 'object'
                    ? v
                    : {
                          label: findLabel(v),
                          value: v,
                      }
            )
        }

        return (
            typeof initData === 'object'
                ? initData
                : {
                      label: findLabel(initData),
                      value: initData,
                  }
        ) as FieldDefinition.DataOption
    }

    private static isNone(v: unknown): v is undefined {
        return pipe(O.fromNullable(v), O.isNone)
    }
}
